import { ComponentType, ReactElement, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AccountTopper, SettingsMenuGroup } from '@tempmee/react-blocks';
import {
  OutlineCursorClickIcon,
  OutlineListIcon,
  OutlineLocationMarkerIcon,
  OutlinePhoneIcon,
  OutlineSettingsIcon,
  SolidToothbrushIcon,
} from '@tempmee/react-icons';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import { ChangeAccountPhotoModal } from '~/components/profile/components/ChangeAccountPhotoModal';
import {
  getSectionValidity,
  locationSettingsKeys,
  toSectionDefaults,
  useOfficeCredentials,
  useOfficeProfile,
  type TLocationSectionId,
} from '~/domain/LocationSettings';
import { useAppSelector } from '~/hooks/useAppSelector';
import { getOfficeAvatarUrl, hasChildOffices } from '~/utils/OfficeUtils';
import {
  fireLocationSettingsSectionCompleted,
  type TLocationSettingsSection,
} from '~/posthogs/events';
import { AccountPageLayout } from '~/components/shared/AccountArea';
import { LocationLoadError } from '~/components/shared/LocationLoadError';
import { BestInDentalWinnerCard } from './components/BestInDentalWinnerCard';
import { useManageRows } from './hooks/useManageRows';
import { useScopedOfficeId } from './hooks/useScopedOfficeId';
import { AddressParkingModal } from './modals/AddressParkingModal';
import { BasicInformationModal } from './modals/BasicInformationModal';
import { ContactModal } from './modals/ContactModal';
import { InsuranceSoftwareModal } from './modals/InsuranceSoftwareModal';
import { OperatingDetailsModal } from './modals/OperatingDetailsModal';
import { ShiftDetailsModal } from './modals/ShiftDetailsModal';
import type { ISectionModalProps } from './modals/types';

interface ISectionEntry {
  id: TLocationSectionId;
  label: string;
  icon: ReactElement;
  testID: string;
  event: TLocationSettingsSection;
  Modal: ComponentType<ISectionModalProps>;
}

/** Figma `Molecule/Account/Menus` Type=Single Location, in its order. */
const SECTIONS: ISectionEntry[] = [
  {
    id: 'basicInformation',
    label: 'Basic Information',
    icon: <SolidToothbrushIcon />,
    testID: 'location-basic-information',
    event: 'basic_information',
    Modal: BasicInformationModal,
  },
  {
    id: 'contact',
    label: 'Contact',
    icon: <OutlinePhoneIcon />,
    testID: 'location-contact',
    event: 'contact',
    Modal: ContactModal,
  },
  {
    id: 'addressParking',
    label: 'Address & Parking',
    icon: <OutlineLocationMarkerIcon />,
    testID: 'location-address-parking',
    event: 'address_parking',
    Modal: AddressParkingModal,
  },
  {
    id: 'shiftDetails',
    label: 'Shift Details',
    icon: <OutlineListIcon />,
    testID: 'location-shift-details',
    event: 'shift_details',
    Modal: ShiftDetailsModal,
  },
  {
    id: 'insuranceSoftware',
    label: 'Insurance & Software',
    icon: <OutlineCursorClickIcon />,
    testID: 'location-insurance-software',
    event: 'insurance_software',
    Modal: InsuranceSoftwareModal,
  },
  {
    id: 'operatingDetails',
    label: 'Operating Details',
    icon: <OutlineSettingsIcon />,
    testID: 'location-operating-details',
    event: 'operating_details',
    Modal: OperatingDetailsModal,
  },
];

interface ILocationDetailPageProps {
  officeId: string;
  /** Back to the locations list; omitted when this is a single-location office's landing page. */
  onBack?: () => void;
}

/**
 * One location's settings (Figma 7169-78327, 7235-93824): its header card, the
 * Manage links and the six Location Details sections. Every save targets this
 * location, whatever the switcher has selected.
 */
export const LocationDetailPage = ({ officeId, onBack }: ILocationDetailPageProps) => {
  const queryClient = useQueryClient();
  const { data: office, isError, isPlaceholderData } = useOfficeProfile(officeId);
  const { data: credentials } = useOfficeCredentials(officeId);
  const scopedOfficeId = useScopedOfficeId();
  const masterOfficeId = useAppSelector((state) => state.user.user?.id);
  const isMultiLocation = useAppSelector((state) => hasChildOffices(state.user.childOffices));
  const isScopedOffice = officeId === scopedOfficeId;
  const manageRows = useManageRows(office);
  const [openSectionId, setOpenSectionId] = useState<TLocationSectionId | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  if (!office) {
    return (
      <AccountPageLayout title="Location settings" onBack={onBack}>
        {isError ? (
          <LocationLoadError />
        ) : (
          <CenteredSpinner />
        )}
      </AccountPageLayout>
    );
  }

  // Sections open, and warn, only on the fetched record: a placeholder may lack
  // office fields, and a dialog seeded from it would start from blanks.
  // Basic Information and Insurance & Software also wait for the credentials.
  const validity =
    credentials && !isPlaceholderData
      ? getSectionValidity(toSectionDefaults(office, credentials))
      : null;
  const openSection = SECTIONS.find((section) => section.id === openSectionId);

  const closePhotoModal = () => {
    setIsPhotoModalOpen(false);
    queryClient.invalidateQueries({ queryKey: locationSettingsKeys.office(officeId) });
  };

  return (
    <>
      <AccountPageLayout
        title={office.office_name}
        onBack={onBack}
        aside={
          <>
            <AccountTopper
              variant="card"
              avatarUrl={getOfficeAvatarUrl(office.profile_image)}
              name={office.office_name}
              address={office.address?.[0]?.formatted_address ?? ''}
              badge={isMultiLocation && office.id === masterOfficeId ? 'ADMIN' : undefined}
              onEditPhotoClick={isPlaceholderData ? undefined : () => setIsPhotoModalOpen(true)}
              testID="account-topper"
            />
            {/* The winner credential reads the selected office, so it shows only on that office's page. */}
            {isScopedOffice && <BestInDentalWinnerCard />}
          </>
        }
      >
        <SettingsMenuGroup heading="Manage" rows={manageRows} testID="location-manage" />
        <SettingsMenuGroup
          heading="Location Details"
          testID="location-details"
          rows={SECTIONS.map((section) => ({
            id: section.id,
            label: section.label,
            icon: section.icon,
            alert: validity && !validity[section.id] ? 'warning' : undefined,
            onSelect: () => setOpenSectionId(section.id),
            disabled: isPlaceholderData,
            testID: section.testID,
          }))}
        />
      </AccountPageLayout>

      {openSection && (
        <openSection.Modal
          officeId={officeId}
          office={office}
          credentials={credentials}
          onClose={() => setOpenSectionId(null)}
          onSaved={() => {
            fireLocationSettingsSectionCompleted({
              section: openSection.event,
              is_selected_office: isScopedOffice,
            });
            setOpenSectionId(null);
          }}
        />
      )}

      {isPhotoModalOpen && <ChangeAccountPhotoModal office={office} onClose={closePhotoModal} />}
    </>
  );
};
